import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Dropdown} from "primereact/dropdown";
import {TabPanel, TabView} from "primereact/tabview";
import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import {ADDITIONAL_LEGAL_RIGHTS_FIELDS as REGISTER_APPRECIATION_FIELDS} from "@reva/shared/register";

import AppreciationInputFormField from "../../component/appreciation/AppreciationInputFormField";
import AppreciationTabInputText from "../../component/appreciation/AppreciationTabInputText";
import DocumentUploader from "../../component/DocumentUploader";
import LoadingIndicator from "../../component/LoadingIndicator";
import {useMultipleParameterValues} from "../../component/paramInput/paramHooks";
import {TabHeaderWithTooltip} from "../../component/TabHeaderWithTooltip";
import ValueLendingValueInfo from "../../component/ValueLendingValueInfo";
import {useSelectByCurrentLanguage} from "../../hooks/languageHooks";
import {selectAddressData} from "../../redux/addressSlice";
import {selectDocuments, selectInitialized, selectLoading} from "../../redux/documentsSlice";
import {downloadDocumentAsync, loadDocumentsAsync} from "../../redux/documentsThunk";
import {selectVisibleObjectParametersCurried} from "../../redux/objectParametersSelectors";
import {selectPersistenceInitialized, selectPersistenceLoading, selectReadOnly} from "../../redux/persistenceSlice";
import {selectHasValueOrders} from "../../redux/valueOrdersSlice";
import ErrorMessageContainer from "./ErrorMessageContainer";
import {CollapsableSection, ObjectInputsBlock, SectionTitle} from "./ObjectInputs";
import PersistenceFinalMessage from "./PersistenceFinalMessage";
import SnapshotMessage from "./SnapshotMessage";
import {RegisterAssetsInput, RegisterOutputAssets, useRegisterAssetTotals} from "./TabRegister/AssetComponents";
import {RegisterOutputSectionII, RegisterSectionIIInput} from "./TabRegister/SectionIIComponents";

/**
 * Input Section Title for Register Tab.
 * @param {string} title section title
 * @param {string|undefined} [appreciationSection] optional section value for appreciation
 * @param {boolean} firstSection flag whether this is the first section in the column
 * @param {string|null} [collapseKey] optional: when provided, show collapse/expand icon and make header a clickable button
 * @return {Element}
 */
function RegisterTitle({title, appreciationSection, firstSection, collapseKey}) {
    return (
        <SectionTitle
            title={title}
            appreciationTab="register"
            appreciationSection={appreciationSection}
            firstSection={firstSection}
            collapseKey={collapseKey}
        />
    );
}

function RegisterAppreciationsInputBlock() {
    // Diese Felder sind Ausnahmen vom Appreciation-Permission-Gate (#18607) und bleiben
    // für ALLE User sichtbar — gesteuert zentral über APPRECIATION_ALWAYS_VISIBLE_KEYS.
    return (
        <div className="rounded-border bg-surface-100 dark:bg-surface-700 p-4 panelForInputs">
            {REGISTER_APPRECIATION_FIELDS.map(({key: section, label}) => (
                <AppreciationInputFormField
                    key={section}
                    page="object"
                    tab="register"
                    section={section}
                    label={label}
                    placeholder={`Würdigung für ${label} ...`}
                />
            ))}
        </div>
    );
}

export function RegisterInputPanel({registerSpecBlocks}) {
    const readOnly = useSelector(selectReadOnly);
    const address = useSelector(selectAddressData);
    const persistenceLoading = useSelector(selectPersistenceLoading);
    const {t} = useTranslation();

    // #19518: Die Gemarkung steht nur als Vorschlag im leeren Feld "Grundbuch von"; gespeichert wird erst eine
    // Eingabe. Grundbuchbezirk und Gemarkung stimmen nicht immer überein.
    const cadastralDistrict = address?.address?.cadastral_district;
    const suggestionPlaceholders =
        !readOnly && cadastralDistrict
            ? {register_location: t("object.tab_register.location_suggestion", {value: cadastralDistrict})}
            : undefined;

    return (
        <div>
            <RegisterTitle
                title={t("object.tab_register.title.input")}
                firstSection={true}
                appreciationSection="grundbuchblatt"
                collapseKey="register_grundbuchblatt"
            />
            <CollapsableSection collapseKey="register_grundbuchblatt">
                <ObjectInputsBlock block={registerSpecBlocks[0]} suggestionPlaceholders={suggestionPlaceholders} />
            </CollapsableSection>

            <RegisterTitle
                title={t("object.tab_register.title.assets")}
                appreciationSection="bestandsverzeichnis"
                collapseKey="register_bestandsverzeichnis"
            />
            <CollapsableSection collapseKey="register_bestandsverzeichnis">
                <RegisterAssetsInput columns={registerSpecBlocks[1].rows} />
            </CollapsableSection>

            <RegisterTitle
                title={t("object.tab_register.title.section_ii")}
                appreciationSection="abteilung_ii"
                collapseKey="register_abteilung_ii"
            />
            <CollapsableSection collapseKey="register_abteilung_ii">
                <RegisterSectionIIInput columns={registerSpecBlocks[2].rows} loading={persistenceLoading} />
            </CollapsableSection>

            {registerSpecBlocks[3] == null ? null : (
                <div>
                    <RegisterTitle
                        title={t("object.tab_register.title.leasehold")}
                        collapseKey="register_erbbaurecht"
                    />
                    <CollapsableSection collapseKey="register_erbbaurecht">
                        <ObjectInputsBlock block={registerSpecBlocks[3]} id={"register_leasehold_input"} />
                    </CollapsableSection>
                </div>
            )}

            <RegisterTitle
                title={t("object.tab_register.title.lasten")}
                appreciationSection="lasten"
                collapseKey="register_lasten"
            />
            <CollapsableSection collapseKey="register_lasten">
                <RegisterAppreciationsInputBlock />
            </CollapsableSection>
        </div>
    );
}

/**
 * Grundbuchblatt-Ausgabe als gestreifte Tabelle, inkl. Grundstücksfläche und Miteigentumsanteil
 * (Summen aus dem Bestandsverzeichnis, gehören inhaltlich zum Grundbuchblatt).
 */
function RegisterOutputGrundbuchblattTable({rows}) {
    const {t} = useTranslation();
    const getByLanguage = useSelectByCurrentLanguage();
    const {parameterValues} = useMultipleParameterValues(
        rows.map((row) => row.parameter),
        "REGISTER",
    );
    const totals = useRegisterAssetTotals();
    const tableRows = [
        ...rows.map((row) => ({
            name: getByLanguage(row.details.nameDe, row.details.nameEn),
            value: parameterValues[row.parameter]?.activeParameterValue || t("object.tab_register.no_entry"),
        })),
        ...totals,
    ];

    return (
        <DataTable value={tableRows} size="small" showHeaders={false} stripedRows>
            <Column field="name" className="font-bold" />
            <Column field="value" bodyClassName="p-align-right" />
        </DataTable>
    );
}

function RegisterOutputView({registerSpecBlocks}) {
    const {t} = useTranslation();
    return (
        <div>
            <RegisterTitle title={t("object.tab_register.title.input")} collapseKey="register_output_grundbuchblatt" />
            <CollapsableSection collapseKey="register_output_grundbuchblatt">
                <RegisterOutputGrundbuchblattTable rows={registerSpecBlocks[0].rows} />
            </CollapsableSection>

            <RegisterTitle
                title={t("object.tab_register.title.assets")}
                collapseKey="register_output_bestandsverzeichnis"
            />
            <CollapsableSection collapseKey="register_output_bestandsverzeichnis">
                <RegisterOutputAssets columns={registerSpecBlocks[1].rows} />
            </CollapsableSection>

            <RegisterTitle
                title={t("object.tab_register.title.section_ii")}
                collapseKey="register_output_abteilung_ii"
            />
            <CollapsableSection collapseKey="register_output_abteilung_ii">
                <RegisterOutputSectionII columns={registerSpecBlocks[2].rows} />
            </CollapsableSection>
        </div>
    );
}

function RegisterDocumentView({documents}) {
    const hasValueOrders = useSelector(selectHasValueOrders);
    const {t} = useTranslation();
    const [selectedName, setSelectedName] = useState();
    const document = documents.find((doc) => doc.name === selectedName) ?? documents[0];

    if (!document) {
        return hasValueOrders ? (
            <div>
                <p className="mb-6">{t("object.tab_register.no_extract")}</p>
                <DocumentUploader documentType={"LandRegister"} />
            </div>
        ) : (
            <p>{t("object.tab_register.no_order_id")}</p>
        );
    }
    return (
        <div>
            {documents.length > 1 && (
                <>
                    <Dropdown
                        value={document.name}
                        options={documents.map((doc) => doc.name)}
                        onChange={(e) => setSelectedName(e.value)}
                    />
                    <div className="m-6" />
                </>
            )}
            <iframe title={document.name} src={document.url} width="100%" style={{height: "90vh"}} />
        </div>
    );
}

export function RegisterOutputPanel({registerSpecBlocks, loading}) {
    const documents = useSelector(selectDocuments);
    const dispatch = useDispatch();
    const [pdfs, setPdfs] = useState([]);
    const {t} = useTranslation();
    const registerDocuments = useRef([]);
    registerDocuments.current = documents?.filter((document) => document?.documentType === "Grundbuchauszug") ?? [];
    // Neu laden nur, wenn sich die Grundbuchauszüge selbst ändern, nicht bei jedem anderen Dokument
    const registerNames = registerDocuments.current.map((document) => document.filename).join("\n");

    useEffect(() => {
        let cancelled = false;
        const urls = [];
        registerDocuments.current.forEach((document) => {
            dispatch(downloadDocumentAsync(document))
                .then((blob) => {
                    if (cancelled) return;
                    const url = URL.createObjectURL(blob);
                    urls.push(url);
                    setPdfs((prev) =>
                        prev.some((pdf) => pdf.name === document.filename)
                            ? prev
                            : [...prev, {url, name: document.filename}],
                    );
                })
                .catch(console.error);
        });
        return () => {
            cancelled = true;
            urls.forEach((url) => URL.revokeObjectURL(url));
            setPdfs([]);
        };
    }, [registerNames, dispatch]);

    if (loading || (pdfs.length === 0 && registerDocuments.current.length > 0)) {
        return <LoadingIndicator loading={loading} />;
    }

    return (
        <TabView className="registerOutputTabView sticky top-0" scrollable>
            <TabPanel header={<TabHeaderWithTooltip i18nPrefix="object.tab_register.output" />}>
                <RegisterOutputView registerSpecBlocks={registerSpecBlocks} />
            </TabPanel>
            <TabPanel header={<span className="tabViewTitle">{t("object.tab_register.title.extract")}</span>}>
                <RegisterDocumentView documents={pdfs} />
            </TabPanel>
        </TabView>
    );
}

/**
 * @returns {React.JSX.Element}
 */
function TabObjectRegister() {
    const registerSpec = useSelector(selectVisibleObjectParametersCurried("object/register", "all"));
    const loading = useSelector(selectLoading);
    const initialized = useSelector(selectInitialized);
    const persistenceInitialized = useSelector(selectPersistenceInitialized);
    const dispatch = useDispatch();

    useEffect(() => {
        if (persistenceInitialized && !initialized && !loading) dispatch(loadDocumentsAsync());
    }, [persistenceInitialized, initialized, loading, dispatch]);

    const registerSpecBlocks = registerSpec?.[0]?.blocks;
    if (!registerSpecBlocks || registerSpecBlocks.length < 3) {
        return null;
    }

    return (
        <div id="RegisterPanelTitle">
            <PersistenceFinalMessage />
            <SnapshotMessage />
            <AppreciationTabInputText page="object" tab="register" />
            <ValueLendingValueInfo />
            <ErrorMessageContainer />

            <div className="grid grid-cols-12 gap-4">
                <div className="col-span-6">
                    <RegisterInputPanel registerSpecBlocks={registerSpecBlocks} />
                </div>
                <div className="col-span-6">
                    <RegisterOutputPanel registerSpecBlocks={registerSpecBlocks} loading={loading || !initialized} />
                </div>
            </div>
        </div>
    );
}

export default TabObjectRegister;
